import { useEffect, useState } from 'react';
import {
  EllipsisOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  MenuOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Button, Collapse, Dropdown, Menu, Modal, PageHeader, Space, Spin, Tooltip, Typography } from 'antd';
import { NewGridPage } from './new-grid-page';
import { NewGridGroupPage } from './new-grid-group-page';
import { useHistory } from 'react-router-dom';
import { apiClient, apiErrorMessage, parseHyperlink } from '../../../utils';
import { Grid, GridGroup } from '../../../models';
import { EntityChangeLog } from '../../reports';
import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';
import { GridDetailsForm } from './grid-details-form';
import './grids-page.scoped.scss';

export function GridsPage() {
  const { Text } = Typography;

  const [addGridModalOpen, setAddGridModalOpen] = useState<{ isOpen: boolean; level?: string[] }>();
  const [editGridModalOpen, setEditModalOpen] = useState<{ grid: Grid; level?: string[] }>();
  const [addGridGroupModalOpen, setAddGridGroupModalOpen] = useState<{ isOpen: boolean; level?: string[] }>();
  const [editGridGroupModalOpen, setEditGridGroupModalOpen] = useState<{ gridGroup: GridGroup; level?: string[] }>();
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<{ entityType: string; recordId: string }>();
  const [loading, setLoading] = useState(false);
  const [grids, setGrids] = useState<Grid[]>();
  const [gridGroups, setGridGroups] = useState<GridGroup[]>();
  const [scheme, setScheme] = useState<any[]>();

  const history = useHistory();

  useEffect(() => {
    loadGrids();
    loadGridGroups();
  }, []);

  const loadGrids = () => {
    setLoading(true);
    apiClient
      .get('grids')
      .then(response => {
        const responseGrids: Grid[] = response.data;
        const responseData = responseGrids.map(grid => {
          return {
            key: grid.id,
            ...grid,
          };
        });
        setScheme(undefined);
        setGrids(responseData);
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const loadGridGroups = () => {
    apiClient
      .get('grid-groups')
      .then(response => {
        setGridGroups(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  };

  useEffect(() => {
    const newScheme: any[] = [];
    if (gridGroups) {
      gridGroups.forEach(gg => {
        newScheme.push({ id: gg.id, itemType: 'gridGroup', children: [], order: gg.order });
      });
    }

    if (grids) {
      grids.forEach(g => {
        if (!g.groupId) {
          newScheme.push({ id: g.id, itemType: 'grid', order: g.order });
        } else {
          const groupIndex = newScheme.findIndex(s => s.id === g.groupId);
          if (groupIndex !== -1) {
            newScheme[groupIndex].children.push({ id: g.id, itemType: 'grid', order: g.order });
          }
        }
      });
    }
    newScheme.sort((a, b) => a.order - b.order);
    newScheme.forEach(s => {
      if (s.children) {
        s.children.sort((a: any, b: any) => a.order - b.order);
      }
    });
    setScheme(newScheme);
  }, [grids, gridGroups]);

  const openAddGridModal = (level?: string[]) => {
    setAddGridModalOpen({ isOpen: true, level });
  };

  const closeAddGridModal = () => {
    setAddGridModalOpen(undefined);
  };

  const openEditGridModal = (grid: Grid, level?: string[]) => {
    setEditModalOpen({ grid, level });
  };

  const closeEditGridModal = () => {
    setEditModalOpen(undefined);
    setLoading(false);
  };

  const openAdGridGroupModal = (level?: string[]) => {
    setAddGridGroupModalOpen({ isOpen: true, level });
  };

  const closeAddGridGroupModal = () => {
    setAddGridGroupModalOpen(undefined);
  };

  const openEditGridGroupModal = (gridGroup: GridGroup, level?: string[]) => {
    setEditGridGroupModalOpen({ gridGroup, level });
  };

  const closeEditGridGroupModal = () => {
    setEditGridGroupModalOpen(undefined);
  };

  const openChangeLogModal = (entityType: string, recordId: string) => {
    setChangeLogModalOpen({ entityType, recordId });
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const onRefreshClick = () => {
    loadGrids();
  };

  const onGridChanged = () => {
    closeAddGridModal();
    loadGrids();
    setLoading(false);
  };

  const onGridGroupChanged = () => {
    closeAddGridModal();
    loadGridGroups();
    setLoading(false);
  };

  const onGridAdded = () => {
    closeAddGridModal();
    closeAddGridGroupModal();
    loadGrids();
    loadGridGroups();
  };

  const deleteGridGroup = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`grid-groups/${id}`)
      .then(() => {
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmDeleteGrid = (id: string) => {
    const onOk = () => deleteGrid(id);
    Modal.confirm({
      title: 'Veri seti sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Veri setini silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const confirmDeleteGridGroup = (id: string) => {
    const onOk = () => deleteGridGroup(id);
    Modal.confirm({
      title: 'Veri seti grubu sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Veri seti grubunu silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const deleteGrid = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`grids/${id}`)
      .then(() => {
        setLoading(false);
        loadGrids();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const updateGridOrder = (scheme: any) => {
    setLoading(true);
    apiClient
      .patch('grid-groups/update-order', { scheme })
      .then(() => {
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const onGridNameClick = (grid: Grid, level?: string[]) => {
    history.push(`/admin/grids/${grid.id}`);
  };

  const onGridGroupNameClick = (gridGroup: GridGroup, level?: string[]) => {
    openEditGridGroupModal(gridGroup, level);
  };

  const onGridActionClick = (e: any, id: string, level: string[]) => {
    const grid = grids?.find(m => m.id === id);
    if (e.key === 'edit') {
      openEditGridModal(grid as Grid, level);
    } else if (e.key === 'changelog') {
      openChangeLogModal('Grid', id);
    } else if (e.key === 'del') {
      confirmDeleteGrid(id);
    }
  };

  const onGridGroupActionClick = (e: any, id: string, level: string[]) => {
    if (e.key === 'edit') {
      const gridGroup = gridGroups?.find(m => m.id === id);
      openEditGridGroupModal(gridGroup as GridGroup, level);
    } else if (e.key === 'changelog') {
      openChangeLogModal('Grid', id);
    } else if (e.key === 'del') {
      confirmDeleteGridGroup(id);
    }
  };

  const gridTableActions = (id: string, level: string[]) => (
    <Menu onClick={(e: any) => onGridActionClick(e, id, level)}>
      <Menu.Item key="edit">Düzenle</Menu.Item>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      <Menu.Item key="del">
        <Text type="danger">Sil</Text>
      </Menu.Item>
    </Menu>
  );

  const gridGroupTableActions = (id: string, level: string[]) => (
    <Menu onClick={(e: any) => onGridGroupActionClick(e, id, level)}>
      <Menu.Item key="edit">Düzenle</Menu.Item>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      <Menu.Item key="del">
        <Text type="danger">Sil</Text>
      </Menu.Item>
    </Menu>
  );

  const onSortEnd = (level: string[], { oldIndex, newIndex }: any) => {
    if (oldIndex === newIndex) {
      return;
    }
    let newScheme = JSON.parse(JSON.stringify(scheme)) as any[];
    let newSubScheme = newScheme;
    if (level?.length) {
      for (const levelItem of level) {
        const updatedNode = newSubScheme.find(s => s.id === levelItem) as any;
        if (!updatedNode.children) {
          updatedNode.children = [];
        }
        newSubScheme = updatedNode.children as any[];
      }
    }
    const newOrderedSubScheme = arrayMove([].concat(newSubScheme as never[]), oldIndex, newIndex).filter(el => !!el);
    newSubScheme.push(...newOrderedSubScheme);
    newSubScheme = newSubScheme.splice(0, newOrderedSubScheme.length);
    newScheme.forEach((s, i) => {
      s.order = i;
      if (s.children) {
        s.children.forEach((c: any, j: number) => {
          c.order = j;
        });
      }
    });

    setScheme(newScheme);
    updateGridOrder(newScheme);
  };

  return (
    <>
      <div
        style={{
          padding: '1rem',
        }}
      >
        <PageHeader
          style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 0 }}
          extra={[
            <Text key="gridCount">
              <b>Veri seti sayısı:</b> {grids?.length || 0}
            </Text>,
            <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
              Yenile
            </Button>,
            <Button key="addGrid" onClick={() => openAddGridModal()} icon={<PlusOutlined />} type="primary">
              Yeni veri seti
            </Button>,
            <Button key="addGridGroup" onClick={() => openAdGridGroupModal()} icon={<PlusOutlined />} type="primary">
              Yeni veri seti grubu
            </Button>,
          ]}
        />
        <Spin spinning={loading}>
          {scheme && (
            <ItemsPanel
              allItems={{ grids: grids, gridGroups: gridGroups }}
              level={[]}
              panelScheme={scheme}
              onGridNameClick={onGridNameClick}
              onGridGroupNameClick={onGridGroupNameClick}
              onSortEnd={onSortEnd}
              gridTableActions={gridTableActions}
              gridGroupTableActions={gridGroupTableActions}
            />
          )}
        </Spin>
        <Modal
          title="Yeni veri seti"
          visible={addGridModalOpen?.isOpen}
          width="50%"
          footer={null}
          onCancel={closeAddGridModal}
          destroyOnClose={true}
        >
          <NewGridPage onSaved={onGridAdded} gridGroups={gridGroups} />
        </Modal>
        <Modal
          title="Veri seti düzenle"
          visible={editGridModalOpen !== undefined}
          width="50%"
          footer={null}
          onCancel={closeEditGridModal}
          destroyOnClose={true}
        >
          <GridDetailsForm grid={editGridModalOpen?.grid} onSaved={onGridChanged} />
        </Modal>
        <Modal
          title="Yeni veri seti grubu"
          visible={addGridGroupModalOpen?.isOpen}
          width="50%"
          footer={null}
          onCancel={closeAddGridGroupModal}
          destroyOnClose={true}
        >
          <NewGridGroupPage onSaved={onGridAdded} />
        </Modal>
        <Modal
          title="Veri seti grubu düzenle"
          visible={editGridGroupModalOpen !== undefined}
          footer={null}
          onCancel={closeEditGridGroupModal}
          destroyOnClose={true}
          width={'50%'}
        >
          <NewGridGroupPage
            onSaved={onGridGroupChanged}
            gridGroups={gridGroups}
            scheme={scheme}
            level={editGridGroupModalOpen?.level}
            gridGroup={editGridGroupModalOpen?.gridGroup}
          />
        </Modal>
        <Modal
          title="Veri seti değişim geçmişi"
          visible={changeLogModalOpen !== undefined}
          footer={null}
          onCancel={closeChangeLogModal}
          destroyOnClose={true}
          width={'80%'}
        >
          <EntityChangeLog entityName={changeLogModalOpen?.entityType || ''} recordId={changeLogModalOpen?.recordId} />
        </Modal>
      </div>
    </>
  );
}

function ItemsPanel(props: {
  allItems?: any;
  level: string[];
  panelScheme?: any[];
  gridTableActions: any;
  gridGroupTableActions: any;
  onGridNameClick: (grid: Grid, level?: string[]) => void;
  onGridGroupNameClick: (gridGroup: GridGroup, level?: string[]) => void;
  onSortEnd: (level: string[], { oldIndex, newIndex }: any) => void;
}) {
  const [grids, setGrids] = useState<Grid[]>();
  const [gridGroups, setGridGroups] = useState<GridGroup[]>();
  const [panelScheme, setPanelScheme] = useState<any[]>();

  useEffect(() => {
    setGrids(props.allItems?.grids);
  }, [props.allItems?.grids]);

  useEffect(() => {
    setGridGroups(props.allItems?.gridGroups);
  }, [props.allItems?.gridGroups]);

  useEffect(() => {
    setPanelScheme(props.panelScheme);
  }, [props.panelScheme]);

  const SortableItem = SortableElement(({ item }: any) => {
    if (item?.itemType === 'grid') {
      const grid = grids?.find(f => f.id === item.id);
      if (grid) {
        return GridTr({ grid, level: props.level, onGridNameClick: props.onGridNameClick, tableActions: props.gridTableActions });
      }
    } else if (item?.itemType === 'gridGroup') {
      const gridGroup = gridGroups?.find(g => g.id === item.id);
      if (gridGroup) {
        return GridGroupTr({
          grid: props.allItems,
          gridGroup,
          level: props.level,
          panelScheme: panelScheme?.find(s => s.id === gridGroup.id)?.children as any[],
          gridTableActions: props.gridTableActions,
          gridGroupTableActions: props.gridGroupTableActions,
          onGridNameClick: props.onGridNameClick,
          onGridGroupNameClick: props.onGridGroupNameClick,
          onSortEnd: props.onSortEnd,
        });
      }
    }
    return null;
  });

  const SortableList = SortableContainer(({ scheme }: any) => {
    return (
      <table key={props.level.join('-')} style={{ width: '100%' }}>
        <tbody>
          {scheme?.map((item: any, i: number) => (
            <SortableItem key={`item-${item.id}`} index={i} item={item} />
          ))}
        </tbody>
      </table>
    );
  });

  const onSortEnd = ({ oldIndex, newIndex }: any) => {
    props.onSortEnd(props.level || [], { oldIndex, newIndex });
  };

  return <SortableList scheme={panelScheme} useDragHandle disableAutoscroll helperClass="row-dragging" onSortEnd={onSortEnd} />;
}

function GridGroupTr(props: {
  grid?: Grid;
  gridGroup: GridGroup;
  level: string[];
  panelScheme?: any[];
  gridTableActions: any;
  gridGroupTableActions: any;
  onGridGroupNameClick: (gridGroup: GridGroup, level?: string[]) => void;
  onGridNameClick: (grid: Grid, level?: string[]) => void;
  onSortEnd: (level: string[], { oldIndex, newIndex }: any) => void;
}) {
  const [isActive, setIsActive] = useState(true);
  const [level, setLevel] = useState<string[]>([]);
  const [panelScheme, setPanelScheme] = useState<any[]>();

  const { Text } = Typography;

  useEffect(() => {
    setPanelScheme(props.panelScheme);
  }, [props.panelScheme]);

  useEffect(() => {
    setLevel(props.level);
  }, [props.level]);

  const DragHandle = SortableHandle(() => <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />);

  const onChange = () => {
    setIsActive(!isActive);
  };

  return (
    <tr className="grid-group">
      <td colSpan={2} style={{ width: '10000rem' }}>
        <Collapse
          activeKey={isActive ? props.gridGroup.id : undefined}
          onChange={onChange}
          style={{ borderColor: props.gridGroup.hexCodeBackground }}
        >
          <Collapse.Panel
            style={{ backgroundColor: props.gridGroup.hexCodeBackground, borderColor: props.gridGroup.hexCodeBackground }}
            header={
              <Space>
                <Button
                  type="link"
                  style={{ padding: 0, height: 'auto', border: 0, fontWeight: 700, color: props.gridGroup.hexCodeTitle }}
                  onClick={() => props.onGridGroupNameClick(props.gridGroup, props.level)}
                >
                  {props.gridGroup.name}
                </Button>
                <Tooltip title={props.gridGroup.description}>
                  <Text type="secondary">
                    <InfoCircleOutlined />
                  </Text>
                </Tooltip>
              </Space>
            }
            key={props.gridGroup.id}
            extra={
              <Space>
                <Dropdown overlay={props.gridGroupTableActions(props.gridGroup.id, props.level)} trigger={['click']}>
                  <Button size="small" icon={<EllipsisOutlined />} />
                </Dropdown>
                <DragHandle />
              </Space>
            }
          >
            <>
              <ItemsPanel
                allItems={props.grid}
                level={[...level, props.gridGroup.id]}
                panelScheme={panelScheme}
                onGridNameClick={props.onGridNameClick}
                onGridGroupNameClick={props.onGridGroupNameClick}
                onSortEnd={props.onSortEnd}
                gridTableActions={props.gridTableActions}
                gridGroupTableActions={props.gridGroupTableActions}
              />
            </>
          </Collapse.Panel>
        </Collapse>
      </td>
    </tr>
  );
}

function GridTr(props: { grid: Grid; level: string[]; tableActions?: any; onGridNameClick: (grid: Grid, level?: string[]) => void }) {
  const { Text } = Typography;

  const DragHandle = SortableHandle(() => <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />);

  return (
    <tr className="grid">
      <td>
        <Space direction="vertical" size={0} style={{ paddingLeft: '2.5rem' }}>
          <Space>
            <Button
              type="link"
              style={{ padding: 0, height: 'auto', border: 0, fontWeight: 600 }}
              onClick={() => props.onGridNameClick(props.grid, props.level)}
            >
              {props.grid.name}
            </Button>
            <Tooltip title={parseHyperlink(props.grid.description)}>
              <Text type="secondary">
                <InfoCircleOutlined />
              </Text>
            </Tooltip>
          </Space>
          <div style={{ fontSize: '0.8rem' }}>
            <Text type="secondary">{props.grid.description}</Text>
          </div>
        </Space>
      </td>
      <td>
        <div style={{ textAlign: 'right', paddingRight: '1rem' }}>
          <Space>
            <Dropdown overlay={props.tableActions(props.grid.id, props.level)} trigger={['click']}>
              <Button size="small" icon={<EllipsisOutlined />} />
            </Dropdown>
            <DragHandle />
          </Space>
        </div>
      </td>
    </tr>
  );
}
